import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { AlertCircle, Bike, Calculator, Calendar, CheckCircle, ChevronDown, Clock, CreditCard, Package, Receipt, Shield, Truck, User } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { API_ENDPOINTS, buildApiUrl } from '../config/api';
import { useTranslation } from '../hooks/useTranslation';
import { computePortalDepositEuros } from '../utils/partialDeposit';
import { formatPriceWithEuro, formatWholeEurosWithEuro } from '../utils/priceFormatter';
import { notifySupportApiNetworkError, notifySupportApiServerError } from '../utils/supportApiErrorNotifier';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);
// PAGO PARCIAL
const DEFAULT_PARTIAL_MIN = 100;
const DEFAULT_PARTIAL_PCT = 20;

const vehicleIdKey = (id) => {
  if (id == null || id === '') return '';
  return String(id);
};

const CheckoutForm = ({ bookingData, onPaymentSuccess, onPaymentError, partialConfig }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [acceptedCancellationPolicy, setAcceptedCancellationPolicy] = useState(false);
  const [paymentPlan, setPaymentPlan] = useState('FULL');
  const { t } = useTranslation();

  const effectiveConfig = partialConfig ?? {
    partialMinTotalEur: DEFAULT_PARTIAL_MIN,
    bikePartialDepositPercent: DEFAULT_PARTIAL_PCT,
  };
  const grandTotal = Number(bookingData?.pricing?.grandTotal) || 0;
  const canPartial = grandTotal > effectiveConfig.partialMinTotalEur;

  useEffect(() => {
    if (!canPartial && paymentPlan === 'PARTIAL') setPaymentPlan('FULL');
  }, [canPartial, paymentPlan]);

  const amountToCharge = useMemo(() => {
    if (!canPartial || paymentPlan !== 'PARTIAL') return Math.round(grandTotal * 100) / 100;
    return computePortalDepositEuros(grandTotal, effectiveConfig.bikePartialDepositPercent);
  }, [canPartial, paymentPlan, grandTotal, effectiveConfig.bikePartialDepositPercent]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    // Validar que se haya aceptado la política de cancelación
    if (!acceptedCancellationPolicy) {
      setError(t('payment.error.cancellation.policy.required'));
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const response = await fetch(buildApiUrl(API_ENDPOINTS.PAYMENT_CREATE_INTENT), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bookingId: bookingData.bookingId,
          amount: amountToCharge,
          customerEmail: bookingData.customerInfo.email,
          paymentMode: paymentPlan,
        })
      });

      if (!response.ok) {
        notifySupportApiServerError(response.status);
        const errorData = await response.json();
        throw new Error(errorData.message || t('payment.error.create'));
      }

      const { clientSecret } = await response.json();

      // Confirmar el pago con Stripe
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: bookingData.customerInfo.name,
            email: bookingData.customerInfo.email,
          },
        }
      });

      if (stripeError) {
        setError(stripeError.message);
        onPaymentError?.(stripeError);
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        // Confirmar el pago en el backend
        await confirmPayment(paymentIntent.id);
      } else {
        setError(t('payment.error.incomplete'));
        onPaymentError?.(new Error(t('payment.error.incomplete')));
      }

    } catch (error) {
      notifySupportApiNetworkError(error);
      setError(error.message);
      onPaymentError?.(error);
    } finally {
      setProcessing(false);
    }
  };

  const confirmPayment = async (paymentIntentId) => {
    const response = await fetch(buildApiUrl(API_ENDPOINTS.PAYMENT_CONFIRM), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        bookingId: bookingData.bookingId,
        paymentIntentId: paymentIntentId,
        temporaryBlockIds: bookingData.temporaryBlockIds
      })
    });

    if (!response.ok) {
      notifySupportApiServerError(response.status);
      const errorData = await response.json();
      throw new Error(errorData.message || t('payment.error.confirm'));
    }

    const result = await response.json();
    onPaymentSuccess?.(result);
  };

  const payButtonAmountLabel =
    canPartial && paymentPlan === 'PARTIAL'
      ? formatWholeEurosWithEuro(amountToCharge)
      : formatPriceWithEuro(grandTotal);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Información de pago */}
      <div className="flex items-center justify-center gap-2 text-xs sm:text-sm text-gray-300 mb-4">
        <CheckCircle size={16} className="text-green-400" />
        <span>{t('payment.secure')}</span>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        {canPartial && (
          <div className="space-y-3 p-4 bg-background border border-gray-custom rounded-lg">
            <p className="text-sm font-medium text-2c3e50">{t('payment.plan.title')}</p>
            <div className="space-y-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="payment-plan"
                  className="mt-1"
                  style={{ accentColor: '#EBC122' }}
                  checked={paymentPlan === 'FULL'}
                  onChange={() => setPaymentPlan('FULL')}
                />
                <span className="text-sm text-2c3e50">{t('payment.plan.full')}</span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="payment-plan"
                  className="mt-1"
                  style={{ accentColor: '#EBC122' }}
                  checked={paymentPlan === 'PARTIAL'}
                  onChange={() => setPaymentPlan('PARTIAL')}
                />
                <span className="text-sm text-2c3e50">{t('payment.plan.partial')}</span>
              </label>
            </div>
            {paymentPlan === 'PARTIAL' && (
              <p className="text-xs text-6c757d leading-relaxed">
                {t('payment.plan.partial.note', {
                  percent: effectiveConfig.bikePartialDepositPercent,
                  amount: formatWholeEurosWithEuro(amountToCharge),
                })}
              </p>
            )}
          </div>
        )}

        <div className="space-y-2 sm:space-y-3">
          <label className="block text-sm font-medium text-2c3e50">
            <div className="flex items-center gap-2">
              <CreditCard size={16} className="text-6c757d" />
              {t('payment.card.info')}
            </div>
          </label>
          <div className="border border-gray-custom rounded-lg p-3 sm:p-4 bg-background shadow-sm">
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#2c3e50',
                    '::placeholder': {
                      color: '#6c757d',
                    },
                  },
                  invalid: {
                    color: '#e74c3c',
                  },
                },
              }}
            />
          </div>
        </div>

        {/* Checkbox de aceptación de políticas de cancelación */}
        <div className="space-y-2 sm:space-y-3">
          <div className="flex items-start gap-3 p-4 bg-background border border-gray-custom rounded-lg">
            <input
              type="checkbox"
              id="cancellation-policy"
              checked={acceptedCancellationPolicy}
              onChange={(e) => setAcceptedCancellationPolicy(e.target.checked)}
              className="mt-1 w-4 h-4 border-gray-300 rounded focus:ring-2 cursor-pointer"
              style={{ 
                accentColor: '#EBC122',
                color: acceptedCancellationPolicy ? '#EBC122' : '#6b7280'
              }}
            />
            <label htmlFor="cancellation-policy" className="flex-1 text-sm sm:text-base text-2c3e50 cursor-pointer">
              {t('payment.cancellation.policy.accept')}{' '}
              <a
                href={t('payment.cancellation.policy.url')}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#EBC122] hover:underline font-semibold hover:text-[#d4a91e] transition-colors"
              >
                {t('payment.cancellation.policy.link')}
              </a>
            </label>
          </div>
        </div>

        <button
          type="submit"
          disabled={!stripe || processing || !acceptedCancellationPolicy}
          className="w-full bg-primary hover:bg-primary-dark text-primary-dark font-semibold py-3 sm:py-4 px-6 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {processing ? (
            <div className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-dark"></div>
              <span>{t('payment.processing')}</span>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2">
              <CreditCard size={16} />
              <span>{t('payment.pay')} {payButtonAmountLabel}</span>
            </div>
          )}
        </button>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 sm:p-4">
            <div className="flex gap-2 items-center text-red-400">
              <AlertCircle size={16} />
              <span className="text-sm">{error}</span>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

const StripePayment = ({ bookingData, onPaymentSuccess, onPaymentError }) => {
  const { t } = useTranslation();
  const [partialConfig, setPartialConfig] = useState(null);
  const [openVehicleKey, setOpenVehicleKey] = useState(null);

  const vehiclesItems = bookingData.pricing?.vehicles?.items ?? [];
  const allAccessoryItems = bookingData.pricing?.accessories?.items ?? [];

  const extrasByVehicleKey = useMemo(() => {
    const map = new Map();
    for (const item of allAccessoryItems) {
      const k = vehicleIdKey(item.vehicleId);
      if (!k) continue;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(item);
    }
    return map;
  }, [allAccessoryItems]);

  useEffect(() => {
    setOpenVehicleKey((prev) => {
      if (prev != null) return prev;
      const first = bookingData.pricing?.vehicles?.items?.[0];
      return first?.id != null ? vehicleIdKey(first.id) : null;
    });
  }, [bookingData.pricing?.vehicles?.items]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(buildApiUrl(API_ENDPOINTS.BOOKING_PARTIAL_CONFIG));
        if (!res.ok) {
          notifySupportApiServerError(res.status);
          if (!cancelled) setPartialConfig(null);
          return;
        }
        const json = await res.json();
        if (!cancelled && json?.success && json?.data) {
          setPartialConfig(json.data);
        }
      } catch (e) {
        notifySupportApiNetworkError(e);
        if (!cancelled) setPartialConfig(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="payment-info-light">
      <div className="w-full max-w-full mx-auto px-4 sm:px-6 py-6">
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl font-bold text-2c3e50 font-orbitron mb-2">{t('payment.title')}</h2>
          <p className="text-sm sm:text-base text-gray-300">{t('payment.subtitle')}</p>
        </div>

        <div className="space-y-6 sm:space-y-8">
          {/* Resumen de la reserva */}
          <div className="p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-4 sm:mb-6">
              <Receipt size={18} className="text-primary" />
              <h3 className="text-lg font-semibold text-2c3e50">{t('payment.summary.title')}</h3>
            </div>
            
            <div className="space-y-6">
              {/* Información del cliente y fechas */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                <div className="space-y-3 sm:space-y-4">
                  <h4 className="font-semibold text-2c3e50 mb-3 flex items-center gap-2">
                    <User size={16} className="text-gray-300" />
                    {t('payment.summary.customer.data')}
                  </h4>
                  
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex items-center gap-3 p-3 sm:p-4 bg-background border border-gray-custom rounded-lg">
                      <User size={16} className="text-gray-300 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-2c3e50 text-sm sm:text-base truncate">{bookingData.customerInfo?.name}</p>
                        <p className="text-xs sm:text-sm text-gray-400 truncate">{bookingData.customerInfo?.email}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 sm:p-4 bg-background border border-gray-custom rounded-lg">
                      <Calendar size={16} className="text-gray-300 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-2c3e50 text-sm sm:text-base">
                          {new Date(bookingData.dates?.start).toLocaleDateString()} - {new Date(bookingData.dates?.end).toLocaleDateString()}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-400">
                          {bookingData.pricing?.durationInDays} {bookingData.pricing?.durationInDays > 1 ? t('date.days') : t('date.day')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Desglose de precios */}
                <div className="space-y-3 sm:space-y-4">
                  <h4 className="font-semibold text-2c3e50 mb-3 flex items-center gap-2">
                    <Calculator size={16} className="text-gray-300" />
                    {t('payment.summary.price.breakdown')}
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                    {/* Duración */}
                    <div className="flex justify-between items-center gap-2 p-3 sm:p-4 bg-primary/10 border border-primary/20 rounded-lg min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <Clock size={16} className="text-primary flex-shrink-0" />
                        <span className="text-sm sm:text-base font-medium text-2c3e50 truncate">{t('payment.summary.duration')}</span>
                      </div>
                      <span className="font-semibold text-gray-500 text-sm sm:text-base flex-shrink-0">{bookingData.pricing?.durationInDays} {t('date.days')}</span>
                    </div>

                    {/* Total bicicletas */}
                    <div className="flex justify-between items-center gap-2 p-3 sm:p-4 bg-primary/10 border border-primary/20 rounded-lg min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <Bike size={16} className="text-primary flex-shrink-0" />
                        <span className="text-sm sm:text-base font-medium text-2c3e50 line-clamp-2">{t('payment.summary.bikes')} ({bookingData.pricing?.durationInDays} {t('date.days')})</span>
                      </div>
                      <span className="font-semibold text-gray-500 text-sm sm:text-base flex-shrink-0">{formatPriceWithEuro(bookingData.pricing?.vehicles?.total || 0)}</span>
                    </div>

                    {/* Total accesorios (sin seguros) */}
                    {(bookingData.pricing?.accessories?.total - (bookingData.pricing?.accessories?.insuranceTotal ?? 0)) > 0 && (
                      <div className="flex justify-between items-center gap-2 p-3 sm:p-4 bg-primary/10 border border-primary/20 rounded-lg min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <Package size={16} className="text-primary flex-shrink-0" />
                          <span className="text-sm sm:text-base font-medium text-2c3e50 truncate">{t('payment.summary.accessories.total')}</span>
                        </div>
                        <span className="font-semibold text-gray-500 text-sm sm:text-base flex-shrink-0">{formatPriceWithEuro((bookingData.pricing?.accessories?.total || 0) - (bookingData.pricing?.accessories?.insuranceTotal ?? 0))}</span>
                      </div>
                    )}

                    {/* Total seguros */}
                    {(bookingData.pricing?.accessories?.insuranceTotal ?? 0) > 0 && (
                      <div className="flex justify-between items-center gap-2 p-3 sm:p-4 bg-primary/10 border border-primary/20 rounded-lg min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <Package size={16} className="text-primary flex-shrink-0" />
                          <span className="text-sm sm:text-base font-medium text-2c3e50 truncate">{t('payment.summary.insurance.total')}</span>
                        </div>
                        <span className="font-semibold text-gray-500 text-sm sm:text-base flex-shrink-0">{formatPriceWithEuro(bookingData.pricing?.accessories?.insuranceTotal || 0)}</span>
                      </div>
                    )}

                    {/* Total de la entrega */}
                    <div className="flex justify-between items-center gap-2 p-3 sm:p-4 bg-primary/10 border border-primary/20 rounded-lg min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <Truck size={16} className="text-primary flex-shrink-0" />
                        <span className="text-sm sm:text-base font-medium text-2c3e50 truncate">{t('payment.summary.delivery.total')}</span>
                      </div>
                      <span className="font-semibold text-gray-500 text-sm sm:text-base flex-shrink-0">{formatPriceWithEuro(bookingData.pricing?.delivery?.cost || 0)}</span>
                    </div>

                    {/* Total de la reserva */}
                    <div className="flex sm:flex-row sm:justify-between sm:items-center gap-3 p-4 sm:p-6 border border-primary rounded-lg md:col-span-2">
                      <div className="flex flex-col min-w-0">
                        <div className="flex items-center gap-2">
                          <Receipt size={18} className="text-primary flex-shrink-0" />
                          <span className="text-lg font-bold text-gray-500">{t('payment.summary.booking.total')}</span>
                        </div>
                        <span className="text-xs text-gray-400 mt-1 ml-7">{t('payment.vat.included')}</span>
                      </div>
                      <span className="text-2xl font-bold text-gray-500 sm:text-right flex-shrink-0">{formatPriceWithEuro(bookingData.pricing?.grandTotal || 0)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Vehículos en columna completa: acordeón con accesorios y seguros por vehículo */}
              <div className="w-full min-w-0 space-y-3">
                <div className="flex items-center gap-2 mb-1">
                  <Bike size={16} className="text-gray-300" />
                  <span className="font-medium text-2c3e50 text-sm sm:text-base">{t('payment.summary.vehicles.selected')}</span>
                </div>
                <div className="flex flex-col gap-2 w-full">
                  {vehiclesItems.map((vehicle, index) => {
                    const vk = vehicleIdKey(vehicle.id) || `idx-${index}`;
                    const extras = extrasByVehicleKey.get(vehicleIdKey(vehicle.id)) ?? [];
                    const accessoriesOnly = extras.filter((x) => x.category !== 'INSURANCE');
                    const insuranceOnly = extras.filter((x) => x.category === 'INSURANCE');
                    const bikeRent =
                      Math.round((Number(vehicle.totalPrice) || 0) * 100) / 100;
                    const accessoriesSum = accessoriesOnly.reduce(
                      (s, a) => s + (Number(a.totalPrice) || 0),
                      0
                    );
                    const insuranceSum = insuranceOnly.reduce(
                      (s, a) => s + (Number(a.totalPrice) || 0),
                      0
                    );
                    const lineTotal =
                      Math.round((bikeRent + accessoriesSum + insuranceSum) * 100) / 100;
                    const hasExtras = extras.length > 0;
                    const isOpen = openVehicleKey === vk;
                    return (
                      <div
                        key={vk}
                        className="w-full rounded-lg border border-gray-custom bg-background overflow-hidden shadow-sm"
                      >
                        <button
                          type="button"
                          className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-gray-50/80 transition-colors"
                          onClick={() => setOpenVehicleKey((cur) => (cur === vk ? null : vk))}
                          aria-expanded={isOpen}
                        >
                          <div className="flex items-start gap-3 min-w-0 flex-1">
                            <Bike size={18} className="text-primary flex-shrink-0 mt-0.5" />
                            <div className="min-w-0">
                              <p className="font-semibold text-2c3e50 text-sm sm:text-base break-words">{vehicle.model}</p>
                              <p className="text-xs sm:text-sm text-gray-400">
                                {t('payment.summary.size')} {vehicle.size}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                            <span className="text-sm font-bold text-2c3e50 tabular-nums whitespace-nowrap">
                              {formatPriceWithEuro(lineTotal)}
                            </span>
                            <ChevronDown
                              size={20}
                              className={`text-gray-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                              aria-hidden
                            />
                          </div>
                        </button>
                        {isOpen && (
                          <div className="border-t border-gray-custom px-4 py-3 sm:px-5 sm:py-4 bg-[#fafbfc] space-y-4">
                            <div className="flex justify-between gap-3 text-sm">
                              <span className="font-medium text-2c3e50 min-w-0">
                                {t('payment.summary.vehicle.bikeRental')}
                              </span>
                              <span className="flex-shrink-0 font-semibold tabular-nums text-2c3e50">
                                {formatPriceWithEuro(bikeRent)}
                              </span>
                            </div>

                            {!hasExtras ? (
                              <p className="text-sm text-6c757d pt-1 border-t border-dashed border-gray-custom/80">
                                {t('payment.summary.vehicle.noExtras')}
                              </p>
                            ) : (
                              <>
                                {accessoriesOnly.length > 0 && (
                                  <div>
                                    <div className="flex items-center justify-between gap-2 mb-2 pt-1">
                                      <div className="flex items-center gap-2 min-w-0">
                                        <Package size={14} className="text-6c757d flex-shrink-0" />
                                        <span className="text-xs font-semibold uppercase tracking-wide text-6c757d">
                                          {t('payment.summary.vehicle.accessoriesOnly')}
                                        </span>
                                      </div>
                                      {accessoriesSum > 0 && (
                                        <span className="text-xs font-semibold tabular-nums text-2c3e50 flex-shrink-0">
                                          {formatPriceWithEuro(accessoriesSum)}
                                        </span>
                                      )}
                                    </div>
                                    <ul className="space-y-2">
                                      {accessoriesOnly.map((a, i) => (
                                        <li
                                          key={`${vk}-a-${a.id}-${i}`}
                                          className="flex justify-between gap-3 text-sm text-2c3e50"
                                        >
                                          <span className="min-w-0 break-words">
                                            {a.name}
                                            <span className="text-gray-400 text-xs ml-1">
                                              ({a.priceType === 'DAILY' ? t('payment.summary.daily') : t('payment.summary.fixed')})
                                            </span>
                                          </span>
                                          <span className="flex-shrink-0 font-medium tabular-nums">
                                            {formatPriceWithEuro(a.totalPrice ?? 0)}
                                          </span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                {insuranceOnly.length > 0 && (
                                  <div>
                                    <div className="flex items-center justify-between gap-2 mb-2 pt-1">
                                      <div className="flex items-center gap-2 min-w-0">
                                        <Shield size={14} className="text-6c757d flex-shrink-0" />
                                        <span className="text-xs font-semibold uppercase tracking-wide text-6c757d">
                                          {t('payment.summary.vehicle.insuranceOnly')}
                                        </span>
                                      </div>
                                      {insuranceSum > 0 && (
                                        <span className="text-xs font-semibold tabular-nums text-2c3e50 flex-shrink-0">
                                          {formatPriceWithEuro(insuranceSum)}
                                        </span>
                                      )}
                                    </div>
                                    <ul className="space-y-2">
                                      {insuranceOnly.map((a, i) => (
                                        <li
                                          key={`${vk}-i-${a.id}-${i}`}
                                          className="flex justify-between gap-3 text-sm text-2c3e50"
                                        >
                                          <span className="min-w-0 break-words">
                                            {a.name}
                                            <span className="text-gray-400 text-xs ml-1">
                                              ({a.priceType === 'DAILY' ? t('payment.summary.daily') : t('payment.summary.fixed')})
                                            </span>
                                          </span>
                                          <span className="flex-shrink-0 font-medium tabular-nums">
                                            {formatPriceWithEuro(a.totalPrice ?? 0)}
                                          </span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                <div className="flex justify-between gap-3 pt-3 mt-1 text-sm font-semibold text-2c3e50">
                                  <span>{t('payment.summary.vehicle.subtotalVehicle')}</span>
                                  <span className="tabular-nums">{formatPriceWithEuro(lineTotal)}</span>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Formulario de pago */}
          <div className="rounded-xl p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-4 sm:mb-6">
              <CreditCard size={20} className="text-primary" />
              <h3 className="text-lg font-semibold text-2c3e50">{t('payment.info.title')}</h3>
            </div>

            {/* Formulario de pago con Stripe Elements */}
            <Elements stripe={stripePromise}>
              <CheckoutForm
                bookingData={bookingData}
                onPaymentSuccess={onPaymentSuccess}
                onPaymentError={onPaymentError}
                partialConfig={partialConfig}
              />
            </Elements>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StripePayment;